/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import type { EdsIconsEnum, SimpleCtaSectionFragment } from "@cosmos/hygraph-schema";
import { CosmosEnumEdsGradients, CosmosEnumSimpleCtaStyle, CosmosEnumSimpleCtaVariant } from "@cosmos/hygraph-schema";

import {
	ButtonList,
	CosmosDesc,
	CosmosText,
	EdsIcon,
	ResponsiveContainer,
	SectionContainer,
	TitleStack,
} from "@components/elements";
import type { CosmosButtonProps } from "@components/elements/atoms/button/CosmosButton.atom";
import type { SectionContainerProps } from "@components/elements/atoms/container/SectionContainer.atom";
import { css, cva } from "@panda/css";
import { Box, VStack } from "@panda/jsx";
import { token } from "@panda/tokens";
import { CSSProperties } from "react";

type SimpleCtaBackgroundImage = {
	url?: string;
	optUrl?: string;
	altText?: string | null;
};

type SimpleCtaSectionProps = Omit<SimpleCtaSectionFragment, "__typename" | "id"> & {
	sectionContainerProps?: SectionContainerProps;
	icon?: EdsIconsEnum | null;
	leftDesc?: string | null;
	backgroundImage?: SimpleCtaBackgroundImage | null;
};
const sectionStyles = css({
	minHeight: "450px",
	display: "flex",
	alignItems: "center",
});

const styled = cva({
	base: {
		"& .simple-cta-stack": {
			display: "flex",
			rowGap: "eds.dimension.24",
			"& .cta-title": {
				textStyle: "heading2XL",
				smDown: {
					textStyle: "headingXL",
				},
			},
			"& .paragraph": {
				textStyle: "paragraphMD",
				smDown: {
					textStyle: "paragraphMD",
				},
			},
			"& a[href]:not(.cosmos-button)": {
				_hover: {
					color: "eds.text.primary",
				},
			},
			'& a[href="#"]:not(.cosmos-button)': {
				pointerEvents: "none",
				textDecoration: "none",
				cursor: "default",
			},
		},
	},
	variants: {
		style: {
			colorful: {
				background: "var(--cosmos-gradient)",
				"& .simple-cta-stack": {
					"& a:not(.cosmos-button)": {
						color: "eds.text.onAccent",
					},
					"& .cta-eyebrow, & .paragraph": {
						color: "eds.text.onAccent",
					},
				},
			},
			simple: {
				backgroundColor: "var(--transparent-light-005, rgba(255, 255, 255, 0.05))",
				"& .heading": {
					"& a:not(.cosmos-button)": {
						background: "var(--cosmos-gradient)",
						backgroundClip: "text",
						color: "transparent",
					},
				},
				"& .cta-eyebrow, & .paragraph": {
					color: "eds.text.primary",
				},
			},
		},
		variant: {
			leftToRight: {
				"& .simple-cta-stack": {
					display: "flex",
					justifyContent: "start",
					"& .heading, & .paragraph": {
						textAlign: "start",
						maxWidth: "600px",
					},
				},
			},
			centered: {
				"& .simple-cta-stack": {
					display: "flex",
					justifyContent: "center",
					"& .heading, & .paragraph": {
						textAlign: "center",
						maxW: "840px", //no eds.dimension
					},
				},
				"& .section-container": {
					justifyContent: "center",
				},
			},
			twoColumn: {
				md: {
					"& .simple-cta-stack": {
						display: "flex",
						flexDirection: "row",
						"& .stack-col": {
							flexBasis: "100%",
							alignSelf: "stretch",
							justifyContent: "end",
						},
					},
				},
			},
		},
	},
});

interface CssVariables extends CSSProperties {
	"--cosmos-gradient"?: string;
}

export const SimpleCtaSection = ({
	style,
	simpleCtaVariant,
	gradient,
	title,
	eyebrow,
	desc,
	buttons,
	icon,
	leftDesc,
	backgroundImage,
	sectionContainerProps,
}: SimpleCtaSectionProps): React.JSX.Element | null => {
	if (!style) style = CosmosEnumSimpleCtaStyle.Colorful;
	if (!simpleCtaVariant) simpleCtaVariant = CosmosEnumSimpleCtaVariant.LeftToRight;
	if (!gradient) gradient = CosmosEnumEdsGradients.Indigo;

	const cosmosButtons: CosmosButtonProps[] = buttons.map((button) => {
		return {
			...button,
			size: "lg",
			variant: button?.variant ?? "contrast",
			iconPosition: button?.iconPosition ?? "end",
		};
	});

	if (!icon && !eyebrow && !title?.text && !desc && !leftDesc && !cosmosButtons?.length) return null;

	const alignItems = simpleCtaVariant === CosmosEnumSimpleCtaVariant.Centered ? "center" : "start";
	const bgUrl = backgroundImage?.optUrl || backgroundImage?.url;

	return (
		<Box
			className={`simple-cta-section ${styled({ style, variant: simpleCtaVariant as "centered" | "leftToRight" })}`}
			style={
				{
					"--cosmos-gradient": token(`gradients.eds.gradient.${gradient}`),
					...(bgUrl
						? {
								backgroundImage: `url("${bgUrl}")`,
								backgroundRepeat: "no-repeat",
								backgroundSize: "cover",
							}
						: {}),
				} as CssVariables
			}
		>
			<ResponsiveContainer>
				<SectionContainer {...sectionContainerProps} className={sectionStyles}>
					<VStack alignItems={alignItems} gap="eds.dimension.24" className="simple-cta-stack">
						<VStack gap="eds.dimension.24" alignItems={alignItems} className="stack-col">
							{icon ? (
								<Box className="cta-icon">
									<EdsIcon icon={icon} size="xl" color="primary" />
								</Box>
							) : null}
							{eyebrow ? <CosmosText variant="headingSM" text={eyebrow || ""} className="cta-eyebrow" /> : null}
							{title?.text ? <TitleStack gradient={gradient} title={title} className="cta-title" /> : null}
							{leftDesc ? <CosmosDesc description={{ html: leftDesc }} className="cta-left-desc" /> : null}
						</VStack>
						<VStack gap="eds.dimension.24" alignItems={alignItems} className="stack-col">
							{desc ? <CosmosDesc description={{ html: desc }} /> : null}
							{cosmosButtons?.length ? <ButtonList buttons={cosmosButtons} /> : null}
						</VStack>
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</Box>
	);
};
